// src/components/explorers/wildfireExplorer/wildfireConfig.ts
// Configuration for Wildfire Explorer using native UDL

import { BaseExplorerConfig, FieldTransformer } from '../../../types/baseExplorerConfig';
import { WildfireEntity } from '../../../types/wildfire';
import { wildfireServiceAdapter } from './wildfireServiceAdapter';
import { faFireFlameCurved } from '@fortawesome/free-solid-svg-icons';
import { WILDFIRE_COLUMNS } from '../../../shared/wildfireSchema';
import { gridColumns } from '../../../shared/columnSchema';
/**
 * Field transformers for wildfire data display
 */

const fireNumberTransformer: FieldTransformer = {
    transform: (value: string) => value || 'Unknown',
    reverseTransform: (value: any) => String(value || '')
};

const sizeTransformer: FieldTransformer = {
    transform: (value: number | null) => {
        if (value === null || value === undefined) return 'N/A';
        return `${value.toFixed(1)} Ha`;
    },
    reverseTransform: (value: any) => {
        const str = String(value || '').replace(' Ha', '');
        return isNaN(Number(str)) ? 0 : Number(str);
    }
};

import { WILDFIRE_STATUS_LABELS, RESPONSE_CATEGORY_LABELS } from '../../../shared/wildfireTransforms';

const statusTransformer: FieldTransformer = {
    transform: (value: string) => WILDFIRE_STATUS_LABELS[value] || value || 'Unknown',
    reverseTransform: (value: any) => String(value || '')
};

const dateTransformer: FieldTransformer = {
    transform: (value: string | null) => {
        if (!value) return 'N/A';
        try {
            // Handle "DD/MMM/YYYY" format from MongoDB
            return value;
        } catch {
            return value;
        }
    },
    reverseTransform: (value: any) => String(value || '')
};

// Special transformer for nested region from objective
const regionTransformer: FieldTransformer = {
    transform: (value: any, entity: any) => {
        // Extract from nested objective.region.name
        if (entity?.objective?.region?.name) {
            return entity.objective.region.name;
        }
        if (entity?.objective?.region?.shortNameAndName) {
            return entity.objective.region.shortNameAndName;
        }
        return value || 'Unknown';
    },
    reverseTransform: (value: any) => String(value || '')
};

const causeTransformer: FieldTransformer = {
    transform: (value: string | null) => value || 'Under Investigation',
    reverseTransform: (value: any) => String(value || '')
};

/**
 * Clean Wildfire Configuration - Native UDL Implementation
 *
 * Uses actual MongoDB/JSONB field names (camelCase) from the data column
 */
export const createWildfireConfig = (): BaseExplorerConfig<WildfireEntity> => ({
    // Entity configuration
    entityName: 'Wildfire',
    complexityLevel: 'moderate',
    autoRefreshIntervalMs: 300000,

    // Core fields to display - USING JSONB FIELD NAMES (camelCase)
    displayFields: [
        'number',              // Fire number (was fire_number)
        'wildfireStatus',      // Status (was wildfire_status)
        'estimatedSize',       // Size (was estimated_size)
        'estimatedStartDate',  // Start date (was estimated_start_date)
        'cause',               // Cause (same)
        'subCause',            // Sub-cause (was sub_cause)
        'objective',           // For region extraction (nested)
        'discoverySize',       // Discovery size (was discovery_size)
        'outDate',             // Out date (was out_date)
        'outSize',             // Final size (was out_size)
        'response'             // #771 — response category, decoded server-side
    ],

    // Service adapter using native UDL
    serviceAdapter: wildfireServiceAdapter,

    // Field transformations for display - USING JSONB FIELD NAMES
    fieldTransformers: {
        number: fireNumberTransformer,
        estimatedSize: sizeTransformer,
        wildfireStatus: statusTransformer,
        estimatedStartDate: dateTransformer,
        objective: regionTransformer,  // Extracts nested region
        cause: causeTransformer,
        subCause: causeTransformer,
        discoverySize: sizeTransformer,
        outDate: dateTransformer,
        outSize: sizeTransformer
    },

    // Explorer features
    features: {
        pagination: {
            enabled: true,
            defaultPageSize: 25,
            pageSizeOptions: [10, 25, 50, 100],
            showPageInfo: true
        },
        sorting: {
            enabled: true,
            multiColumn: false,
            // #567 — operators want newest fires on top; sorting by fire_number
            // ordered alphabetically by zone prefix which felt random.
            defaultSort: [{ field: 'estimatedStartDate', direction: 'desc' }]
        },
        searching: {
            enabled: true,
            placeholder: 'Search fires by number, cause, region...',
            searchFields: ['number', 'cause', 'subCause', 'wildfireStatus'],  // Changed field names
            debounceMs: 300
        },
        filtering: {
            enabled: true,
            advancedFilters: false,
            quickFilters: [
                {
                    field: 'wildfireStatus',
                    label: 'Status',
                    type: 'dropdown',
                    options: [
                        { value: 'OUT_OF_CONTROL', label: '🔥 Out of Control' },
                        { value: 'UNDER_CONTROL', label: '🟡 Under Control' },
                        { value: 'CLOSED', label: '✅ Closed' },
                        { value: 'BEING_MONITORED', label: '👀 Being Monitored' },
                        { value: 'DECLARED_OUT', label: '✅ Declared Out' }
                    ]
                },
                {
                    // #771 — options derive from the shared label map so the
                    // dropdown can never drift from what the column renders.
                    field: 'response',
                    label: 'Response',
                    type: 'dropdown',
                    options: Object.values(RESPONSE_CATEGORY_LABELS)
                        .sort()
                        .map((label) => ({ value: label, label }))
                },
                {
                    field: 'cause',
                    label: 'Cause',
                    type: 'dropdown',
                    options: [
                        { value: 'Natural', label: 'Natural (Lightning)' },
                        { value: 'Human Caused', label: 'Human-Caused' },
                        { value: 'Holdover', label: 'Holdover' }
                    ]
                },
                { field: 'number', label: 'Fire Number', type: 'textSearch' },
                { field: 'estimatedSize', label: 'Size', type: 'numeric' },
                { field: 'estimatedStartDate', label: 'Start Date', type: 'dateRange' },
                { field: 'subCause', label: 'Sub-Cause', type: 'textSearch' },
                { field: 'objective', label: 'Region', type: 'textSearch' },
                { field: 'discoverySize', label: 'Discovery Size', type: 'numeric' },
                { field: 'outDate', label: 'Out Date', type: 'dateRange' },
                { field: 'outSize', label: 'Final Size', type: 'numeric' }
            ]
        },
        export: {
            enabled: true,
            formats: ['csv', 'excel'],
            includeFilters: true,
            entity: 'wildfires'
        },
        editing: {
            enabled: false,
            allowCreate: false,
            allowUpdate: false,
            allowDelete: false,
            mode: 'modal'
        }
    },

    // Presentation configuration
    presentation: {
        title: 'Wildfires',
        subtitle: 'Active and historical wildfire incidents',
        icon: faFireFlameCurved,  // FontAwesome icon
        columnConfig: gridColumns(WILDFIRE_COLUMNS),

        styling: {
            theme: 'default',
            striped: true,
            bordered: true,
            hoverable: true
        },

        layout: {
            responsive: true,
            showFooter: true,
            showToolbar: true,
            minHeight: 400,
            maxHeight: 800
        }
    },


});

// Backward compatibility - default config without custom actions
export const cleanWildfireConfig = createWildfireConfig();